import { notify, confirm } from '../../utils/notify';
import React, { useState, useEffect } from 'react';
import Header from '../Header';
import { expenseAPI } from '../../services/api';
import { useListControls, ListToolbar, Pagination } from '../../components/ListControls';
import Topbar from "../../components/Topbar";

const money = n => new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(Number(n)||0);

function ExpenseReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const lc = useListControls(claims, { searchFields: ['claimId', r=>r.employeeName||r.requestedBy?.userName, 'department', 'purposeOfTravel'], dateField: 'createdAt', pageSize: 15 });

  useEffect(() => {
    Promise.all([expenseAPI.reports({ type:'detailed' }), expenseAPI.listClaims({ limit:100 })])
      .then(([r,c]) => { setData(r); setClaims(c.claims||[]); })
      .catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const statusColor = { SETTLED:'#16a34a', FINANCE_APPROVED:'#2563eb', MANAGER_APPROVED:'#f59e0b', SUBMITTED:'#6b7280', REJECTED:'#dc2626', DRAFT:'#9ca3af' };

  return (
    <>
      <Header/>
      <div id="db-wrapper"><main id="page-content">
          <Topbar section="eTAMS" /><section className="container-fluid p-4 pt-3">
        <div className="page-hero"><div><h5 className="text-white lh-1">Expense Reports</h5><div className="sub">Summary of travel expenses, advances and settlements</div></div></div>

        {loading?<div className="text-center py-5"><span className="spinner-border text-primary"/></div>:(
          <>
            <div className="row g-2 mb-4">
              {[{label:'Total Expenses',val:money(data?.summary?.totalExpenses||0),bg:'#eff6ff',ic:'#3b82f6',icon:'bi-receipt'},{label:'Total Advances',val:money(data?.summary?.totalAdvances||0),bg:'#fffbeb',ic:'#f59e0b',icon:'bi-wallet2'},{label:'Total Settled',val:money(data?.summary?.settled||0),bg:'#f0fdf4',ic:'#22c55e',icon:'bi-check2-all'},{label:'Total Claims',val:data?.summary?.claimCount||0,bg:'#f3e8ff',ic:'#7c3aed',icon:'bi-files'}].map((s,i)=>(
                <div key={i} className="col-6 col-md-3">
                  <div className="kpi-card d-flex align-items-center gap-2">
                    <div className="kpi-icon" style={{background:s.bg}}><i className={`bi ${s.icon}`} style={{color:s.ic}}/></div>
                    <div><div style={{fontSize:'0.946rem',fontWeight:700,lineHeight:1}}>{s.val}</div><div style={{fontSize:'0.7rem',color:'#6b7280',marginTop:2}}>{s.label}</div></div>
                  </div>
                </div>
              ))}
            </div>

            {data?.byDepartment&&Object.keys(data.byDepartment).length>0&&(
              <div className="kpi-card mb-3">
                <div style={{fontWeight:700,fontSize:'0.81rem',color:'#111827',marginBottom:8}}>Department-wise Expenses</div>
                {Object.entries(data.byDepartment).sort((a,b)=>b[1]-a[1]).map(([dept,amt])=>(
                  <div key={dept} className="mb-2">
                    <div className="d-flex justify-content-between mb-1" style={{fontSize:'0.736rem'}}>
                      <span style={{color:'#374151',fontWeight:600}}>{dept}</span><span style={{color:'#6b7280'}}>{money(amt)}</span>
                    </div>
                    <div style={{height:6,background:'#f3f4f6',borderRadius:3}}>
                      <div style={{height:'100%',background:'linear-gradient(90deg,#1a5276,#1e8449)',borderRadius:3,width:`${Math.min(100,(amt/(data.summary?.totalExpenses||1))*100)}%`}}/>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="kpi-card">
              <div style={{fontWeight:700,fontSize:'0.81rem',color:'#111827',marginBottom:8}}>All Claims</div>
              {claims.length===0?<div className="empty-state"><i className="bi bi-inbox"/><p>No claims</p></div>:(
                <>
                <ListToolbar search={lc.search} setSearch={lc.setSearch} dateFrom={lc.dateFrom} setDateFrom={lc.setDateFrom} dateTo={lc.dateTo} setDateTo={lc.setDateTo} placeholder="Search by Claim ID, employee, department..." dateLabel="Date"/>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',fontSize:'0.718rem',borderCollapse:'collapse'}}>
                    <thead><tr style={{background:'#f9fafb'}}>{['Claim ID','Employee','Department','Purpose','Total','Advance','Net Payable','Status','Date'].map(h=><th key={h} style={{padding:'6px 7px',fontWeight:600,color:'#6b7280',textAlign:'left',border:'1px solid #f3f4f6',whiteSpace:'nowrap'}}>{h}</th>)}</tr></thead>
                    <tbody>{lc.paginated.map((r,i)=>(
                      <tr key={r._id} style={{background:i%2===0?'#fff':'#fafafa'}}>
                        <td style={{padding:'6px 7px',border:'1px solid #f3f4f6',color:'#1d4ed8',fontWeight:600}}>{r.claimId}</td>
                        <td style={{padding:'6px 7px',border:'1px solid #f3f4f6'}}>{r.employeeName||r.requestedBy?.userName}</td>
                        <td style={{padding:'6px 7px',border:'1px solid #f3f4f6'}}>{r.department}</td>
                        <td style={{padding:'6px 7px',border:'1px solid #f3f4f6',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.purposeOfTravel}</td>
                        <td style={{padding:'6px 7px',border:'1px solid #f3f4f6',fontWeight:600}}>{money(r.totalExpenses)}</td>
                        <td style={{padding:'6px 7px',border:'1px solid #f3f4f6'}}>{money(r.advanceReceived)}</td>
                        <td style={{padding:'6px 7px',border:'1px solid #f3f4f6',fontWeight:600,color:r.netPayable>=0?'#16a34a':'#dc2626'}}>{money(Math.abs(r.netPayable))}{r.netPayable<0?' (Rec.)':''}</td>
                        <td style={{padding:'6px 7px',border:'1px solid #f3f4f6'}}><span style={{background:statusColor[r.status]+'20',color:statusColor[r.status],fontSize:'0.626rem',fontWeight:700,padding:'2px 6px',borderRadius:20}}>{r.status}</span></td>
                        <td style={{padding:'6px 7px',border:'1px solid #f3f4f6',whiteSpace:'nowrap'}}>{new Date(r.createdAt).toLocaleDateString('en-IN')}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                <Pagination page={lc.page} totalPages={lc.totalPages} setPage={lc.setPage} totalCount={lc.totalCount} pageSize={lc.pageSize} itemLabel="claims"/>
                </>
              )}
            </div>
          </>
        )}
      </section></main></div>
    </>
  );
}
export default ExpenseReports;
